import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AssignDemandRequest,
  PutLeadPortfolioMetadataRequest,
  TaxonomyCluster,
  getLeadPortfolioMetadata,
  getLeadPortfolioTaxonomyInput,
  getLeadPortfolioTaxonomyTrace,
  postLeadPortfolioAssignDemand,
  postLeadPortfolioRemoveInferredDemand,
  postLeadPortfolioTaxonomyRegenerate,
  putLeadPortfolioMetadata,
  putLeadPortfolioTaxonomy,
} from "../http/leadPortfolio";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

/** Read the persisted portfolio metadata for a tag (display name, default
 *  tenant org, cached taxonomy). Returns null if the tag has no metadata
 *  row yet (which is a normal state for tags applied before the operator
 *  registered them as a portfolio). */
export const useLeadPortfolio = (tag: string) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadPortfolio", tag],
    async () =>
      getLeadPortfolioMetadata(
        await getAuthTokenBlocking(getToken, relogin),
        tag
      ),
    { enabled: !!tag }
  );
};

/** Read the per-site facts the AI model would see + the current match hash.
 *  Used to detect "the cached taxonomy is stale" (membership / HH coverage
 *  changed since last regenerate). */
export const useLeadPortfolioTaxonomyInput = (tag: string) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadPortfolioTaxonomyInput", tag],
    async () =>
      getLeadPortfolioTaxonomyInput(
        await getAuthTokenBlocking(getToken, relogin),
        tag
      ),
    { enabled: !!tag }
  );
};

/** Read the complete AI trace from the last taxonomy generation for a portfolio.
 *  `enabled` is opt-in (the operator clicks "show AI trace") since the trace can be
 *  large. Returns null when no trace is available (e.g. taxonomy copied from prod). */
export const useLeadPortfolioTaxonomyTrace = (tag: string, enabled: boolean) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadPortfolioTaxonomyTrace", tag],
    async () =>
      getLeadPortfolioTaxonomyTrace(
        await getAuthTokenBlocking(getToken, relogin),
        tag
      ),
    { enabled: !!tag && enabled }
  );
};

/** Three mutations bundled together because the portfolio review page uses
 *  all three: PUT metadata (operator edits display name / default org),
 *  POST taxonomy/regenerate (invokes Bedrock), POST assign-demand. */
export const useLeadPortfolioMutations = (tag: string) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries(["leadPortfolio", tag]);
    queryClient.invalidateQueries(["leadPortfolioTaxonomyInput", tag]);
  };

  const updateMetadata = useMutation(
    async (body: PutLeadPortfolioMetadataRequest) =>
      putLeadPortfolioMetadata(
        await getAuthTokenBlocking(getToken, relogin),
        tag,
        body
      ),
    { onSuccess: invalidate }
  );

  // Kicks off the regenerate LRO and returns the operation handle. Invalidation happens when the
  // caller's poll completes (the cached taxonomy isn't fresh until then), not on this 202.
  const regenerateTaxonomy = useMutation(async () =>
    postLeadPortfolioTaxonomyRegenerate(
      await getAuthTokenBlocking(getToken, relogin),
      tag
    )
  );

  // assign + remove are tier-2 LRO kickoffs (each recomputes many sites via update_site). They
  // return an operation handle; invalidation happens when the caller's poll completes.
  const assignDemand = useMutation(async (body: AssignDemandRequest) =>
    postLeadPortfolioAssignDemand(
      await getAuthTokenBlocking(getToken, relogin),
      tag,
      body
    )
  );

  const removeInferredDemand = useMutation(async () =>
    postLeadPortfolioRemoveInferredDemand(
      await getAuthTokenBlocking(getToken, relogin),
      tag
    )
  );

  const saveTaxonomy = useMutation(
    async (clusters: TaxonomyCluster[]) =>
      putLeadPortfolioTaxonomy(
        await getAuthTokenBlocking(getToken, relogin),
        tag,
        clusters
      ),
    { onSuccess: invalidate }
  );

  return {
    updateMetadata,
    regenerateTaxonomy,
    assignDemand,
    removeInferredDemand,
    saveTaxonomy,
  };
};
